import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Loader2 } from "lucide-react";
import type { PropertyAttributeRow } from "@/features/properties/types/propertyAttributes";
import { mergeSurveys } from "../services/mergeSurveys";

interface MergeSurveysDialogProps {
  projectId: string;
  propertyId: string;
  surveys: PropertyAttributeRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the freshly created merged survey (navigates into the editor). */
  onMerged: (survey: PropertyAttributeRow) => void;
}

function surveyLabel(survey: PropertyAttributeRow): string {
  const date = new Date(survey.captured_at);
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} · ${date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}`;
}

export function MergeSurveysDialog({
  projectId,
  propertyId,
  surveys,
  open,
  onOpenChange,
  onMerged,
}: MergeSurveysDialogProps) {
  const queryClient = useQueryClient();
  const [requiredSourceId, setRequiredSourceId] = useState("");
  const [optionalSourceId, setOptionalSourceId] = useState("");
  const [merging, setMerging] = useState(false);

  const sameSurvey = requiredSourceId !== "" && requiredSourceId === optionalSourceId;
  const canMerge = requiredSourceId !== "" && optionalSourceId !== "" && !sameSurvey && !merging;

  const reset = () => {
    setRequiredSourceId("");
    setOptionalSourceId("");
    setMerging(false);
  };

  const handleMerge = async () => {
    const requiredSource = surveys.find((s) => s.id === requiredSourceId);
    const optionalSource = surveys.find((s) => s.id === optionalSourceId);
    if (!requiredSource || !optionalSource) return;

    setMerging(true);
    try {
      const { row, stats } = await mergeSurveys({
        projectId,
        propertyId,
        requiredSource,
        optionalSource,
      });
      queryClient.invalidateQueries({ queryKey: ["property-attributes-list", propertyId] });
      toast.success(
        `Surveys merged (${stats.merged} answer${stats.merged === 1 ? "" : "s"} taken from the web survey)`
      );
      if (stats.unmatched > 0) {
        toast.info(`${stats.unmatched} question(s) had no match in the web survey and kept the app survey's answer`);
      }
      reset();
      onOpenChange(false);
      onMerged(row);
    } catch (err) {
      console.error("Survey merge failed:", err);
      toast.error(err instanceof Error ? err.message : "Failed to merge surveys");
      setMerging(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (merging) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Merge surveys</DialogTitle>
          <DialogDescription>
            Creates a new survey combining both: field questions (required in the
            template) keep the app survey's answers, every other question takes the
            web survey's answers. The merged survey becomes the latest one visible
            to clients right away.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              App survey (field questions) *
            </Label>
            <Select value={requiredSourceId} onValueChange={setRequiredSourceId} disabled={merging}>
              <SelectTrigger aria-label="App survey">
                <SelectValue placeholder="Select survey" />
              </SelectTrigger>
              <SelectContent>
                {surveys.map((survey) => (
                  <SelectItem key={survey.id} value={survey.id}>
                    {surveyLabel(survey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Web survey (all other questions) *
            </Label>
            <Select value={optionalSourceId} onValueChange={setOptionalSourceId} disabled={merging}>
              <SelectTrigger aria-label="Web survey">
                <SelectValue placeholder="Select survey" />
              </SelectTrigger>
              <SelectContent>
                {surveys.map((survey) => (
                  <SelectItem key={survey.id} value={survey.id}>
                    {surveyLabel(survey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {sameSurvey && (
            <p className="text-xs text-destructive">
              Pick two different surveys to merge.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={merging}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleMerge} disabled={!canMerge}>
            {merging ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Merging…
              </>
            ) : (
              "Merge surveys"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
